import PropTypes from 'prop-types';
import React from 'react';

import { Stack } from '@chakra-ui/react';

import { scroller } from 'react-scroll';
import NextLink from 'next/link';

import { Button } from '@outdoorly/button';
import { Text } from '@outdoorly/text';
import { StarRating } from '@outdoorly/star-rating';
import { ProductPrice } from './ProductPrice';
import { useAuth } from '../../providers/auth';
import { usePricing } from '@outdoorly/util';

const PriceHidden = () => (
  <Stack spacing={4} paddingY={4}>
    <Text fontWeight="500" color="scheme.textPrimary" size="lg">
      {"What's the price?"}
    </Text>
    <Text color="scheme.textPrimary">
      {"Sign up or log in to Outdoorly to see this product's price."}
    </Text>
  </Stack>
);

export const ProductHeader = (props) => {
  const { product, definiteCurrentVariant, isDesktop, onReadMore } = props;

  const { isNotAuthed } = useAuth();
  const isSoldOut = !product.inStock;
  const productPricing = usePricing(product);

  const variantPricing = usePricing(definiteCurrentVariant);

  const { undiscountedPrice } = definiteCurrentVariant
    ? variantPricing
    : productPricing;

  return (
    <Stack spacing={4} pt={5} pb={[3, 3, 3, 0]} pl={[4, 4, 4, 0]}>
      <NextLink href={`/brands/${product?.brand.slug}`} passHref>
        <Text
          as="a"
          size="md"
          color="scheme.textSecondary"
          sx={{ marginBottom: '-2px' }}
        >
          {product?.brand.name}
        </Text>
      </NextLink>
      <Text fontWeight="500" size="xl" as="h1" sx={{ lineHeight: '32px' }}>
        {product?.name}
      </Text>
      {(product?.rating || !!product?.reviews.totalCount) && (
        <Stack direction="row" align="center">
          {product?.rating && <StarRating size={5} rating={product?.rating} />}
          {!!product?.reviews.totalCount && (
            <Button
              pt={'6px'}
              alignSelf="flex-start"
              paddingX={0}
              variant="link"
              colorScheme="gray"
              onClick={() => {
                onReadMore?.();
                scroller.scrollTo('reviews', {
                  duration: 600,
                  offset: -25,
                  smooth: 'easeInOut',
                });
              }}
            >
              {`Read ${product.reviews.totalCount} Review${
                product.reviews.totalCount > 1 ? 's' : ''
              }`}
            </Button>
          )}
        </Stack>
      )}
      {isDesktop && undiscountedPrice && (
        <ProductPrice
          product={product}
          definiteCurrentVariant={definiteCurrentVariant}
          isDesktop={isDesktop}
        />
      )}
      {isSoldOut && (
        <Text css={{ whiteSpace: 'nowrap' }} color="scheme.priceSale">
          SOLD OUT
        </Text>
      )}
      {isNotAuthed && <PriceHidden />}
    </Stack>
  );
};

ProductHeader.propTypes = {
  discountedPrice: PropTypes.number,
  isNotAuthed: PropTypes.bool,
  isSoldOut: PropTypes.bool,
  isDesktop: PropTypes.bool,
  product: PropTypes.shape({
    brand: PropTypes.shape({
      name: PropTypes.string,
      slug: PropTypes.string,
    }),
    inStock: PropTypes.bool,
    name: PropTypes.string,
    rating: PropTypes.number,
    reviews: PropTypes.shape({
      totalCount: PropTypes.number,
    }),
  }),
  onReadMore: PropTypes.func,
  definiteCurrentVariant: PropTypes.object,
};
